import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import type { BackupProvider } from './api';
import { useCreateProvider, useUpdateProvider } from './queries';

/**
 * Anlegen und Aendern eines Zugangs.
 *
 * Ein Dialog fuer beides -- zwei Masken mit denselben sechs Feldern waeren
 * zwei Stellen, an denen ein neues Feld vergessen werden kann.
 *
 * **Beim Aendern bleibt das Kennwortfeld leer**, und leer heisst: unveraendert.
 * Eine Maske, die das Kennwort bei jeder Aenderung verlangt, fuehrt dazu, dass
 * niemand mehr etwas aendert -- oder dass jemand ein neues eintraegt, das er
 * sich nicht notiert hat.
 *
 * **Die Partnerkennung ist Pflicht.** Ohne sie lehnt Cove die
 * Statistikabfrage mit Fehler 13501 ab -- und ein Zugang, der sich anmelden
 * kann und nichts liefert, ist im Betrieb der laestigste Fall: Er sieht
 * eingerichtet aus.
 */
export function ProviderDialog({
  provider,
  onClose,
}: {
  provider?: BackupProvider;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateProvider();
  const update = useUpdateProvider();

  const [values, setValues] = useState({
    name: provider?.name ?? 'Cove Backup',
    partner_name: provider?.partner_name ?? '',
    partner_id: provider?.partner_id ?? '',
    api_user: provider?.api_user ?? '',
    password: '',
    base_url: provider?.base_url ?? 'https://api.backup.management/jsonapi',
    is_active: provider?.is_active ?? true,
  });

  const mutation = provider ? update : create;

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const submit = () => {
    const payload = {
      name: values.name.trim(),
      partner_name: values.partner_name.trim(),
      partner_id: values.partner_id.trim(),
      api_user: values.api_user.trim(),
      base_url: values.base_url.trim(),
      is_active: values.is_active,
      // Leer heisst unveraendert -- siehe Modulkopf. Beim Anlegen ist es
      // Pflicht, und der Server weist eine leere Angabe ab.
      ...(values.password ? { password: values.password } : {}),
    };

    if (provider) {
      update.mutate({ id: provider.id, payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>
              {provider ? t('tokens.edit') : t('tokens.add')}
            </h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">
            {t('tokens.subtitle')}
          </Dialog.Description>

          {mutation.isError ? <ErrorNotice error={mutation.error} /> : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            noValidate
          >
            <div className="field">
              <label className="field__label" htmlFor="provider-name">
                {t('masterdata.name')}
              </label>
              <input
                id="provider-name"
                className="field__control"
                value={values.name}
                autoFocus
                onChange={(event) => {
                  set('name', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="provider-partner-name">
                {t('tokens.partnerName')}
              </label>
              <input
                id="provider-partner-name"
                className="field__control"
                value={values.partner_name}
                onChange={(event) => {
                  set('partner_name', event.target.value);
                }}
              />
              <span className="field__hint">{t('tokens.partnerNameHint')}</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="provider-partner-id">
                {t('tokens.partnerId')}
              </label>
              <input
                id="provider-partner-id"
                className="field__control"
                value={values.partner_id}
                onChange={(event) => {
                  set('partner_id', event.target.value);
                }}
              />
              <span className="field__hint">{t('tokens.partnerIdHint')}</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="provider-api-user">
                {t('tokens.apiUser')}
              </label>
              <input
                id="provider-api-user"
                className="field__control"
                value={values.api_user}
                autoComplete="off"
                onChange={(event) => {
                  set('api_user', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="provider-password">
                {t('tokens.password')}
              </label>
              <input
                id="provider-password"
                type="password"
                className="field__control"
                value={values.password}
                autoComplete="new-password"
                onChange={(event) => {
                  set('password', event.target.value);
                }}
              />
              <span className="field__hint">
                {provider ? t('tokens.passwordKeepHint') : t('tokens.passwordHint')}
              </span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="provider-base-url">
                {t('tokens.baseUrl')}
              </label>
              <input
                id="provider-base-url"
                className="field__control"
                value={values.base_url}
                onChange={(event) => {
                  set('base_url', event.target.value);
                }}
              />
            </div>

            <label className="field__checkbox">
              <input
                type="checkbox"
                checked={values.is_active}
                onChange={(event) => {
                  set('is_active', event.target.checked);
                }}
              />
              {t('tokens.active')}
            </label>

            <div className="row">
              <Button type="submit" disabled={mutation.isPending}>
                {t('app.save')}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {t('app.cancel')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
